import { useQuery } from "@tanstack/react-query";
import { GraduationCap } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import type { AlertRecord } from "../shared/schemas";
import { EmptyState, ErrorState, LoadingState, PageHeader, PriorityBadge, StatusBadge } from "../components/ui";

export function BottlenecksPage() {
  const { snapshotTermId, departmentId } = useAppContext();
  const query = useQuery({
    queryKey: ["bottlenecks", snapshotTermId, departmentId],
    queryFn: () => apiFetch<AlertRecord[]>(`/alerts${queryString({ snapshotTermId, department: departmentId })}`),
    enabled: Boolean(snapshotTermId),
    select: (alerts) => alerts.filter((alert) => alert.alertType === "waitlist_pressure"),
  });
  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;
  return (
    <>
      <PageHeader title="Bottleneck Resolver" description="High-priority sections with no available seats and waitlist pressure at or above 80%." />
      {query.data?.length === 0 ? (
        <EmptyState title="No active bottlenecks" description="No sections currently meet the PRD high-waitlist trigger." />
      ) : (
        <div className="panel divide-y divide-slate-100">
          {query.data?.map((alert) => (
            <article key={alert.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
              <div className="rounded-xl bg-amber-50 p-3 text-amber-600"><GraduationCap className="h-5 w-5" /></div>
              <div className="flex-1">
                <h2 className="font-bold text-slate-900">{alert.title}</h2>
                <p className="mt-1 text-sm text-slate-500">{alert.message}</p>
                {alert.departmentCode && <p className="mt-2 text-xs font-semibold text-slate-400">Department {alert.departmentCode}</p>}
              </div>
              <div className="flex gap-2"><PriorityBadge priority={alert.priority} /><StatusBadge status={alert.status} /></div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
